// =============================================================================
// FILE: src/providers/SubscriptionProvider.jsx
// MODULE: Billing — Subscription Provider
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   Loads and exposes the current tenant's subscription state.
//   Calls the get_my_subscription() Supabase RPC function and populates
//   the Zustand store. Exposes useSubscription() hook for gate checks.
//
// WHEN IT RUNS:
//   Only after AuthProvider confirms a valid session and an admin user.
//   Players and teamleaders don't need subscription state — it's skipped for them.
//
// USAGE:
//   const { tier, canCreate, checkGate } = useSubscription()
//   const result = checkGate('canCreateTournament')
//   if (!result.allowed) showUpgradePrompt(result)
// =============================================================================

import { createContext, useContext, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/store/useAppStore'
import { useAuth } from '@/providers/AuthProvider'
import { ROLES } from '@/lib/constants'
import {
  canCreateTournament,
  canUseSport,
  canAddTeamToTournament,
  canAddPlayerToTeam,
  canExportData,
  canAccessAnalytics,
  canCustomizeBranding,
  isPaymentBlocked,
  getTierFeatures,
  getAllTiers,
  getUpgradeMessage,
} from '@/lib/subscription-gate'

// ---------------------------------------------------------------------------
// Context — accessed via useSubscription() hook only
// ---------------------------------------------------------------------------
const SubscriptionContext = createContext(null)

// ---------------------------------------------------------------------------
// SubscriptionProvider
// Place inside AuthProvider but outside the page Router so it loads once.
// ---------------------------------------------------------------------------
export function SubscriptionProvider({ children }) {
  const { isAuthenticated, role } = useAuth()
  const { setSubscription } = useAppStore()

  // -------------------------------------------------------------------------
  // Effect: Load subscription state when user is authenticated.
  // Only runs for admin and super_admin — players don't have subscriptions.
  // Re-runs if authentication state changes (login/logout).
  // -------------------------------------------------------------------------
  useEffect(() => {
    // Skip subscription loading for non-admin roles
    if (!isAuthenticated || !role) return
    if (role === ROLES.PLAYER || role === ROLES.TEAM_LEADER) return

    loadSubscription(setSubscription)
  }, [isAuthenticated, role, setSubscription])

  return (
    <SubscriptionContext.Provider value={{}}>
      {children}
    </SubscriptionContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// INTERNAL: loadSubscription
// Calls the get_my_subscription() DB function and stores the result.
// ---------------------------------------------------------------------------
async function loadSubscription(setSubscription) {
  try {
    const { data, error } = await supabase.rpc('get_my_subscription')

    if (error) {
      console.error('[SubscriptionProvider] Failed to load subscription:', error.message)
      return
    }

    if (data && data.length > 0) {
      setSubscription(data[0])
    }
  } catch (err) {
    console.error('[SubscriptionProvider] Unexpected error:', err)
  }
}

// ---------------------------------------------------------------------------
// useSubscription hook
// Provides subscription state and pre-bound gate check functions.
//
// USAGE PATTERNS:
//   // Simple boolean check
//   const { canExport } = useSubscription()
//   if (!canExport) return <UpgradePrompt />
//
//   // Full gate result with reason and required tier
//   const { checkGate, tenant } = useSubscription()
//   const result = checkGate('canCreateTournament')
//   if (!result.allowed) showUpgradeModal(result)
// ---------------------------------------------------------------------------
export function useSubscription() {
  const context = useContext(SubscriptionContext)

  if (context === null) {
    throw new Error('[Arcus] useSubscription() must be used inside <SubscriptionProvider>.')
  }

  // Pull subscription state from Zustand store
  const {
    subscriptionTier,
    subscriptionStatus,
    activeTournamentCount,
    tenant,
  } = useAppStore()

  // Build the tenant-like object that gate functions expect
  // Gate functions need: subscription_tier, subscription_status, active_tournament_count
  const tenantForGate = {
    subscription_tier:       subscriptionTier,
    subscription_status:     subscriptionStatus,
    active_tournament_count: activeTournamentCount,
  }

  // -------------------------------------------------------------------------
  // checkGate — universal gate checker
  // Takes a gate name (string) and optional arguments, returns a GateResult.
  //
  // @param {string} gateName - Name of the gate function from subscription-gate.js
  // @param {...any} args     - Additional arguments the gate function needs
  // @returns {GateResult}   - { allowed, reason, requiredTier }
  //
  // EXAMPLE:
  //   checkGate('canAddTeamToTournament', currentTeamCount)
  // -------------------------------------------------------------------------
  const checkGate = (gateName, ...args) => {
    const gates = {
      canCreateTournament:      () => canCreateTournament(tenantForGate),
      canUseSport:              (sportKey) => canUseSport(tenantForGate, sportKey),
      canAddTeamToTournament:   (count) => canAddTeamToTournament(tenantForGate, count),
      canAddPlayerToTeam:       (count) => canAddPlayerToTeam(tenantForGate, count),
      canExportData:            () => canExportData(tenantForGate),
      canAccessAnalytics:       () => canAccessAnalytics(tenantForGate),
      canCustomizeBranding:     () => canCustomizeBranding(tenantForGate),
    }

    const gateFn = gates[gateName]
    if (!gateFn) {
      console.warn(`[useSubscription] Unknown gate: "${gateName}"`)
      return { allowed: true, reason: null, requiredTier: null }
    }

    return gateFn(...args)
  }

  // -------------------------------------------------------------------------
  // Pre-computed booleans for the most common checks.
  // Use these in JSX for conditional rendering of locked features.
  // -------------------------------------------------------------------------
  const isPaymentOk     = !isPaymentBlocked(tenantForGate)
  const tierFeatures    = getTierFeatures(subscriptionTier)

  return {
    // Raw state
    tier:                   subscriptionTier,
    status:                 subscriptionStatus,
    activeTournamentCount,

    // Tier feature config
    features:               tierFeatures,
    allTiers:               getAllTiers(),

    // Payment status
    isPaymentOk,
    isPaymentBlocked:       !isPaymentOk,

    // Pre-computed common gates (boolean)
    canExport:              isPaymentOk && tierFeatures.canExportData,
    canAnalytics:           isPaymentOk && tierFeatures.hasAnalytics,
    canBranding:            isPaymentOk && tierFeatures.canCustomizeBranding,

    // Universal gate checker (returns full GateResult)
    checkGate,

    // Message builder for upgrade modals
    getUpgradeMessage: (gateResult) => getUpgradeMessage(gateResult, subscriptionTier),
  }
}
